import React, { useEffect, useState } from "react"
import { t } from '../../components/i18n/i18n'
import back from "../../assets/images/elements/timeHeader.jpg"

const MOBILE_BP = 600

const DAYS = [
  {
    key: 'tue',
    label: () => t("tuesday"),
    accent: '#f07a28',
    icon: '🙏',
    events: [
      { time: () => t("scheduleTueTime1"), desc: () => t("scheduleTueDescription1") },
      { time: () => t("scheduleTueTime2"), desc: () => t("scheduleTueDescription2") },
      { time: () => t("scheduleTueTime3"), desc: () => t("scheduleTueDescription3") },
    ],
  },
  {
    key: 'wed',
    label: () => t("wednesday"),
    accent: '#d4a800',
    icon: '📖',
    events: [
      { time: () => t("scheduleWedTime1"), desc: () => t("scheduleWedDescription1") },
    ],
  },
  {
    key: 'fri',
    label: () => t("friday"),
    accent: '#3aaa6e',
    icon: '✝',
    events: [
      { time: () => t("scheduleFriTime1"), desc: () => t("scheduleFriDescription1") },
    ],
  },
  {
    key: 'sat',
    label: () => t("saturday"),
    accent: '#3a8fd9',
    icon: '👥',
    events: [
      { time: () => t("scheduleSatTime1"), desc: () => t("scheduleSatDescription1") },
      { time: () => t("scheduleSatTime2"), desc: () => t("scheduleSatDescription2") },
      { time: () => t("scheduleSatTime3"), desc: () => t("scheduleSatDescription3") },
    ],
  },
]

const SUNDAY = {
  key: 'sun',
  label: () => t("sunday"),
  accent: '#7c5cbf',
  icon: '⛪',
  events: [
    { time: () => t("scheduleSunTime1"), desc: () => t("scheduleSunDescription1") },
    { time: () => t("scheduleSunTime2"), desc: () => t("scheduleSunDescription2") },
  ],
}

const DayCard = ({ day, full = false, mobile = false }) => (
  <div style={{ ...s.card, ...(full ? s.cardFull : {}) }}>
    <div style={{ ...s.cardHead, background: day.accent }}>
      <span style={s.cardIcon}>{day.icon}</span>
      <span style={s.cardDay}>{day.label()}</span>
    </div>
    <div style={{ ...s.cardBody, ...(full ? s.cardBodyFull : {}) }}>
      {day.events.map((ev, i) => (
        <div key={i} style={mobile ? s.eventMobile : s.event}>
          <span style={{ ...s.timePill, background: day.accent }}>{ev.time()}</span>
          <span style={s.eventDesc}>{ev.desc()}</span>
        </div>
      ))}
    </div>
  </div>
)

export const Schedule = () => {
  const [mobile, setMobile] = useState(window.innerWidth < MOBILE_BP)

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < MOBILE_BP)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <>
      {/* Hero header */}
      <div style={s.hero}>
        <img src={back} alt='' style={s.heroImg} />
        <div style={s.heroOverlay} />
        <h1 style={s.heroTitle}>{t("scheduleHeader")}</h1>
      </div>

      {/* Cards */}
      <div style={s.page}>
        <div style={{ ...s.grid, gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)' }}>
          {DAYS.map(day => <DayCard key={day.key} day={day} mobile={mobile} />)}
        </div>
        <DayCard day={SUNDAY} full mobile={mobile} />
      </div>
    </>
  )
}

const s = {
  /* ── Hero ─────────────────────────────────────────────────────────────── */
  hero: {
    position: 'relative',
    width: '100%',
    minHeight: '180px',
    overflow: 'hidden',
  },
  heroImg: {
    width: '100%',
    minHeight: '180px',
    objectFit: 'cover',
    display: 'block',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(18,8,3,0.45) 0%, rgba(45,31,20,0.7) 100%)',
  },
  heroTitle: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    fontSize: '36px',
    fontWeight: '800',
    color: '#fff',
    textShadow: '0 2px 16px rgba(0,0,0,0.6)',
    letterSpacing: '0.5px',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    textAlign: 'center',
    padding: '0 24px',
  },

  /* ── Layout ───────────────────────────────────────────────────────────── */
  page: {
    maxWidth: '900px',
    margin: '0 auto 60px',
    padding: '28px 16px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },

  /* ── Day card ─────────────────────────────────────────────────────────── */
  card: {
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(92,61,46,0.12)',
    border: '1px solid #f0e4d4',
  },
  cardFull: {
    /* overrides nothing extra — layout handled by parent */
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 20px',
  },
  cardIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  cardDay: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#fff',
    letterSpacing: '0.3px',
    textShadow: '0 1px 4px rgba(0,0,0,0.25)',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    textTransform: 'uppercase',
  },
  cardBody: {
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cardBodyFull: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '12px 40px',
  },

  /* ── Event row ────────────────────────────────────────────────────────── */
  event: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  eventMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  timePill: {
    flexShrink: 0,
    color: '#fff',
    fontSize: '13px',
    fontWeight: '700',
    padding: '4px 12px',
    borderRadius: '20px',
    letterSpacing: '0.5px',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    marginTop: '1px',
  },
  eventDesc: {
    fontSize: '15px',
    color: '#2d1f14',
    lineHeight: '1.5',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
}
